import {
  TermsOfUseDocument,
  type TermsOfUseDocumentProps,
} from './TermsOfUseDocument'
import {
  TermsOfUseAcceptance,
  type TermsOfUseAcceptanceProps,
} from '@/features/account/terms/TermsOfUseAcceptance'
import {
  type TermsOfUseAcceptanceResource,
  type TermsOfUseDocumentResource,
} from '@/features/account/api/resources/tou'
import { collection } from '@/infra/collection'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import { type TermsOfUseApi } from '@/features/account/api/tou-api'
import { safeParseDate } from '@/utils/date-util'
import { runInAction } from 'mobx'
import { isNotNil } from '@/utils/object-util'
import { Subject } from 'rxjs'
import { memoizeAsync } from '@/utils/memoize-decorator'
import type { GetTermsOfUseConfigFn } from '@/features/account/internal'
import { clearMemoized } from '@/utils/memoize-util'
import type { TermsAcceptanceInfo } from '@/features/account'

/**
 * Manages terms of use versions and agreements.
 */
export class TermsOfUseStore {
  /** @see {acceptance$} */
  private readonly _acceptance$ = new Subject<TermsOfUseAcceptance>()

  /**
   * Terms of use documents.
   */
  readonly termsOfUseDocuments = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new TermsOfUseDocument(),
  })

  /**
   * Current user's acceptances of terms.
   */
  readonly termsOfUseAcceptances = collection({
    getDataId: (d) => `${d.workspaceId}/${d.termsOfUseId}`,
    getModelId: (m) => `${m.workspaceId}/${m.termsOfUseId}`,
    create: () => new TermsOfUseAcceptance(),
  })

  /**
   * Emits when the latest terms have been accepted.
   */
  readonly acceptance$ = this._acceptance$.asObservable()

  constructor(
    private readonly _getTermsConfig: GetTermsOfUseConfigFn,
    private readonly _api: TermsOfUseApi,
  ) {}

  /**
   * Lists the user's terms of use acceptances.
   * Also expands the terms document.
   *
   * @param workspaceId
   * @param paging
   * @param signal
   */
  async listTermsOfUseAcceptances(
    workspaceId: Id,
    paging: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<TermsOfUseAcceptance>> {
    workspaceId = Id.assert(workspaceId)
    const response = await this._api.listWorkspaceAcceptances(
      {
        workspace_id: workspaceId,
        limit: paging.limit,
        after: paging.after,
        expand: ['tou'],
      },
      signal,
    )

    return runInAction(() => {
      // We expanded the TOU document, so set those as well.
      this.termsOfUseDocuments.set(
        response.data.map((r) => {
          if (isNotNil(r.tou) && typeof r.tou === 'object') {
            return mapTermsOfUseDocumentResourceToProps(r.tou)
          }
          return null
        }),
      )

      const acceptances = this.termsOfUseAcceptances.set(
        response.data.map(mapTermsOfUseAcceptanceResourceToProps),
      )

      return CursorPaged.of(acceptances, response.cursor)
    })
  }

  /**
   * Fetches the latest terms for the platform, or for the workspace provided.
   * @param workspaceId
   * @param signal
   */
  async fetchLatestTermsOfUseDocument(
    workspaceId: Id,
    signal?: AbortSignal,
  ): Promise<TermsOfUseDocument> {
    const response = await this._api.getLatestTermsOfUseDocument(
      this._workspaceIdBasedOnTermsConfig(workspaceId),
      signal,
    )

    return this.termsOfUseDocuments.set(response)
  }

  /**
   * Checks whether the latest terms are accepted.
   *
   * @param workspaceId Workspace ID to check terms for, or `null` if checking
   *  platform terms.
   * @param signal Abort signal.
   */
  @memoizeAsync()
  async checkTermsOfUseAcceptance(
    workspaceId: Id,
    signal?: AbortSignal,
  ): Promise<boolean> {
    if (!Id.some(workspaceId)) {
      return false
    }

    const response = await this._api.checkAcceptance(
      {
        workspace_id: this._workspaceIdBasedOnTermsConfig(workspaceId),
      },
      signal,
    )

    return response.accepted
  }

  /**
   * Fetches terms acceptance information for the
   * given workspace and current user.
   *
   * @param workspaceId
   * @param signal
   */
  async fetchTermsAcceptance(
    workspaceId: Id,
    signal?: AbortSignal,
  ): Promise<TermsAcceptanceInfo> {
    signal?.throwIfAborted()

    if (!this._getTermsConfig(workspaceId).enabled) {
      return 'NotApplicable'
    }

    const acceptance = await this.checkTermsOfUseAcceptance(workspaceId, signal)
    return acceptance ? 'Accepted' : 'NotAccepted'
  }

  /**
   * Accepts the latest terms for the platform, or for the workspace provided.
   *
   * @param workspaceId
   */
  async acceptLatestTerms(workspaceId: Id) {
    const response = await this._api.acceptLatestTerms({
      workspace_id: this._workspaceIdBasedOnTermsConfig(workspaceId),
    })
    const acceptance = this.termsOfUseAcceptances.set(
      mapTermsOfUseAcceptanceResourceToProps(response),
    )

    // Clear the cached call for checking acceptance.
    clearMemoized(this.checkTermsOfUseAcceptance, workspaceId)
    this._acceptance$.next(acceptance)
    return acceptance
  }

  /**
   * Returns the workspace ID if custom terms are enabled for the workspace,
   * otherwise returns `null`. Used to toggle between global terms
   * and workspace terms based on the configuration.
   * @param workspaceId
   * @private
   */
  private _workspaceIdBasedOnTermsConfig(workspaceId: Id) {
    const config = this._getTermsConfig(workspaceId)
    return config.enabled && config.customTerms ? workspaceId : undefined
  }
}

function mapTermsOfUseDocumentResourceToProps(
  resource: TermsOfUseDocumentResource,
): TermsOfUseDocumentProps {
  return {
    id: resource.id,
    dateCreated: safeParseDate(resource.date_created),
    dateModified: safeParseDate(resource.date_modified),
    markup: resource.markup,
  }
}

function mapTermsOfUseAcceptanceResourceToProps(
  resource: TermsOfUseAcceptanceResource,
): TermsOfUseAcceptanceProps {
  return {
    acceptedAt: safeParseDate(resource.accepted_at),
    termsOfUseId: resource.tou_id,
    workspaceId: resource.workspace_id,
    internalUserId: resource.user_id,
  }
}
